import { alpha, darken, lighten, PaletteColor, PaletteMode, Theme } from '@mui/material'
import type { Customizations } from '@mxenabled/mxui'

import type { ClientStyleProfile } from '../../core'
import { ClientConfig, StyleConfig } from '../../core/types/AppData'

type PaletteOptions = NonNullable<Customizations['paletteOptions']>
type ModeColorOptions = Partial<NonNullable<NonNullable<PaletteOptions['background']>['default']>>

const TYPOGRAPHY_VARIANTS = [
  'fontFamily',
  'h1',
  'h2',
  'h3',
  'body1',
  'body2',
  'button',
  'caption',
  'subtitle1',
  'subtitle2',
  'tiny',
] as const

const PALETTE_CONFIG = [
  { key: 'primary', override: 'primary_color', light: 'primary_color', dark: 'primary_color_dark' },
  {
    key: 'secondary',
    override: 'secondary_color',
    light: 'secondary_color',
    dark: 'secondary_color_dark',
  },
  { key: 'error', override: 'error_color', light: 'error_color', dark: 'error_color_dark' },
  { key: 'warning', override: 'warning_color', light: 'warning_color', dark: 'warning_color_dark' },
  { key: 'info', override: 'info_color', light: 'info_color', dark: 'info_color_dark' },
  { key: 'success', override: 'success_color', light: 'success_color', dark: 'success_color_dark' },
] as const

const SEED_CONFIG = [
  ...PALETTE_CONFIG,
  { key: 'grey', override: 'neutral_color', light: 'neutral_color', dark: 'neutral_color_dark' },
] as const

export const getClientCustomizations = (
  clientStyleProfile: ClientStyleProfile,
  mode: PaletteMode,
  styleConfig?: StyleConfig,
  requestedMode?: ClientStyleProfile['mode'],
): Customizations => {
  const isDark = mode === 'dark'
  const requested = requestedMode ?? clientStyleProfile?.mode ?? mode

  const paletteOptions: PaletteOptions = {}
  for (const { key, override, light, dark } of SEED_CONFIG) {
    const color = styleConfig?.[override] ?? clientStyleProfile?.[isDark ? dark : light]
    if (color) paletteOptions[key] = color
  }

  const useDarkSlot = isDark && requested !== 'browser'
  const asModeColor = (color: string): ModeColorOptions =>
    useDarkSlot ? { dark: color } : { light: color }
  const backgroundColor = styleConfig?.background_color ?? clientStyleProfile?.background_color
  const paperColor = styleConfig?.paper_color ?? clientStyleProfile?.paper_color
  if (backgroundColor || paperColor) {
    paletteOptions.background = {
      ...(backgroundColor && { default: asModeColor(backgroundColor) }),
      ...(paperColor && { paper: asModeColor(paperColor) }),
    } as PaletteOptions['background']
  }

  const fontFamily =
    styleConfig?.font_name || clientStyleProfile?.body_font_name || clientStyleProfile?.system_font
  const headerFontFamily = styleConfig?.header_font_name || clientStyleProfile?.header_font_name

  const useGlass = styleConfig?.use_glass ?? clientStyleProfile?.use_glass
  const cardRadius = styleConfig?.card_corner_radius ?? clientStyleProfile?.card_corner_radius
  // Cards render at 2x the shape token (MXUI's defaults: token 4, Paper 8), so the
  // client's card radius is halved here and applied at full value to MuiCard/MuiPaper
  // via getClientComponentOverrides
  const borderRadius = cardRadius !== undefined ? cardRadius / 2 : undefined

  return {
    ...(Object.keys(paletteOptions).length > 0 && { paletteOptions }),
    ...((fontFamily || headerFontFamily) && {
      typographyOptions: {
        ...(fontFamily && { fontFamily }),
        ...(headerFontFamily && { headerFontFamily }),
      },
    }),
    ...(useGlass !== undefined && { styleOptions: { useGlass } }),
    ...(borderRadius !== undefined && { shapeOptions: { borderRadius } }),
  }
}

export const getClientComponentOverrides = (
  theme: Theme,
  clientStyleProfile: ClientStyleProfile,
  clientConfig?: ClientConfig,
) => {
  const styleConfig = clientConfig?.master?.style

  const baseComponents = {
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: theme.palette.primary.light },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchViewButton: {
          color: theme.palette.primary.lighter,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.1) },
        },
      },
    },
  }

  const buttonRadius = styleConfig?.button_corner_radius ?? clientStyleProfile?.button_corner_radius
  const cardRadius = styleConfig?.card_corner_radius ?? clientStyleProfile?.card_corner_radius

  const components = {
    ...baseComponents,
    ...(buttonRadius !== undefined && {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: `${buttonRadius}px`, textTransform: 'none' },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: `${buttonRadius}px`,
            minWidth: buttonRadius,
            minHeight: buttonRadius,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { borderRadius: `${buttonRadius}px`, textTransform: 'none' },
        },
      },
    }),
    ...(cardRadius !== undefined && {
      MuiCard: {
        styleOverrides: { root: { borderRadius: `${cardRadius}px` } },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: `${cardRadius}px` },
        },
      },
    }),
  }

  return { components }
}

/** @deprecated Use `getClientCustomizations` and pass the result to `createMXTheme` */
export const getClientPaletteOptions = (
  clientStyleProfile: ClientStyleProfile,
  mode: PaletteMode,
): Record<string, string> => {
  const isDark = mode === 'dark'
  const options: Record<string, string> = {}

  for (const { key, light, dark } of SEED_CONFIG) {
    const color = clientStyleProfile?.[isDark ? dark : light] as string | undefined
    if (color) options[key] = color
  }

  return options
}

/** @deprecated Build the theme with `getClientCustomizations` + `createMXTheme` and merge `getClientComponentOverrides` on top */
export const getClientTheme = (
  theme: Theme,
  clientStyleProfile: ClientStyleProfile,
  clientConfig?: ClientConfig,
) => {
  const isDark = theme.palette.mode === 'dark'
  const font =
    (clientConfig?.master?.style?.font_name ||
      clientStyleProfile?.body_font_name ||
      clientStyleProfile?.system_font) ??
    theme.typography.fontFamily

  const generatePalette = (color: string | undefined, defaultColor: PaletteColor) =>
    color
      ? {
          lighter: lighten(color, 0.5),
          light: lighten(color, 0.25),
          main: color,
          dark: darken(color, 0.25),
          darker: darken(color, 0.5),
        }
      : defaultColor

  const palette = Object.fromEntries(
    PALETTE_CONFIG.map(({ key, light, dark }) => [
      key,
      generatePalette(
        clientStyleProfile?.[isDark ? dark : light] as string | undefined,
        theme.palette[key],
      ),
    ]),
  )

  const typography = Object.fromEntries(
    TYPOGRAPHY_VARIANTS.map((variant) => [
      variant,
      variant === 'fontFamily' ? font : { fontFamily: font },
    ]),
  )

  const baseComponents = {
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: palette.primary.light },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchViewButton: {
          color: palette.primary.lighter,
          '&:hover': { backgroundColor: alpha(palette.primary.light, 0.1) },
        },
      },
    },
  }

  const buttonRadius = clientStyleProfile?.button_corner_radius
  const cardRadius = clientStyleProfile?.card_corner_radius

  const components = {
    ...baseComponents,
    ...(buttonRadius && {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: `${buttonRadius}px`, textTransform: 'none' },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: `${buttonRadius}px`,
            minWidth: buttonRadius,
            minHeight: buttonRadius,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { borderRadius: `${buttonRadius}px`, textTransform: 'none' },
        },
      },
    }),
    ...(cardRadius && {
      MuiCard: {
        styleOverrides: { root: { borderRadius: `${cardRadius}px` } },
      },
    }),
    ...(cardRadius && {
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: `${cardRadius}px` },
        },
      },
    }),
  }

  return { typography, palette, components }
}
